/**
 * Assemble one quote item's audit timeline (Stream D, spec_20260808).
 *
 * Pure and display-agnostic: it stitches the feeds the deal modal already holds
 * (the A6 event log, the D4 testing record, the A4 item, the quote's created
 * date) into ordered rows. The component formats currency/dates and renders —
 * this only decides WHAT happened and WHEN, keyed to one item.
 *
 * Rows come out in lifecycle order (quoted -> received -> tested -> adjusted ->
 * paid), which is the chronological order Prism emits these kinds in. Reprice
 * events are deliberately omitted: a reprice line landing right after the tested
 * row confused operators reading the payment modal. Every field is nullable; a
 * row is emitted only when there is evidence it happened.
 *
 * @param {object} args
 * @param {string} args.quoteItemId
 * @param {object|null} args.item              the A4/projected item (buy_price_cents, quoted_buy_price_cents, payment_id)
 * @param {Array<object>} args.events          the quote's A6 events (kind, source, payload, occurred_at)
 * @param {object|null} args.testingRecord     the D4 record for this item (tested_at, tested_by, condition_after)
 * @param {string|null} args.createdate        the quote's created date (fallback for the "quoted" row)
 * @returns {Array<object>} rows: { key, at, ...kind-specific fields }
 */
export function buildItemTimeline({ quoteItemId, item = null, events = [], testingRecord = null, createdate = null, lineage = [] }) {
    const rows = []
    const forItem = kind => events.filter(e => e?.kind === kind && e?.payload?.quote_item_id === quoteItemId)

    // Quoted — the frozen offer, at quote creation, at the declared grade.
    const quotedEvent = events.find(e => e?.kind === 'quote_created')
    rows.push({
        key: 'quoted',
        at: quotedEvent?.occurred_at ?? createdate ?? null,
        amountCents: item?.quoted_buy_price_cents ?? null,
        condition: item?.declared_condition ?? null,
    })

    // Received — the item arrived in the warehouse (batch event, keyed by membership).
    const receivedEvent = events.find(e =>
        e?.kind === 'items_received' && (e?.payload?.quote_item_ids || []).includes(quoteItemId))
    if (receivedEvent) rows.push({ key: 'received', at: receivedEvent.occurred_at ?? null })

    // Tested — the committed payout, found grade, and who tested it. The payout
    // is the ladder-committed BP from the committed event (the authoritative
    // Phase-A value), NEVER item.buy_price_cents: that is committed-else-OFFER,
    // which for a Broken or pre-Phase-A item falls back to the quoted offer and
    // would misreport the offer as the payout (TI-2915B: offer £68 vs real £20).
    // No committed record -> no payout figure here.
    const committedEvent = forItem('committed_payout_recorded')
        .find(e => e.payload?.source !== 'operator_override')
    if (testingRecord || committedEvent) {
        const testedAt = testingRecord?.tested_at ?? committedEvent?.occurred_at ?? null
        rows.push({
            key: 'tested',
            at: testedAt,
            by: testingRecord?.tested_by ?? null,
            found: testingRecord?.condition_after ?? committedEvent?.payload?.found ?? null,
            // The BP the item carried going INTO the test — the "before" the
            // penalty is measured from, and the figure the standalone
            // "Tested: … penalty …" line used to show. Prefer the committed
            // event's authoritative original_bp; else derive it from the feeds
            // we already hold: the last reprice at-or-before the test, else the
            // quoted offer.
            originalBpCents: committedEvent?.payload?.original_bp
                ?? _preTestBuyCents(forItem('item_repriced'), testedAt, item?.quoted_buy_price_cents ?? null),
            // Prism's committed for THIS item (D4 testing record), which is
            // populated for backfilled historical downgrades too — not just
            // post-Phase-A tests that emitted an event. NULL for Broken (spares,
            // Stream A) -> no payout figure, never the offer-fallback item price.
            payoutCents: testingRecord?.committed_payout_cents ?? committedEvent?.payload?.committed_bp ?? null,
        })
    }

    // Issue raised — a test flagged the item into `issue`. "Awaiting customer
    // response" only when a real reduced offer exists (committed < quoted);
    // otherwise "Issue flagged" (no figure to present yet, or no reduction).
    for (const e of forItem('item_issue_raised')) {
        const committed = e.payload?.committed_buy_price_cents ?? null
        const quoted = e.payload?.quoted_buy_price_cents ?? null
        const awaitingCustomer = quoted != null && committed != null && committed < quoted
        rows.push({
            key: 'issue',
            at: e.occurred_at ?? null,
            label: awaitingCustomer ? 'Awaiting customer response' : 'Issue flagged',
            found: e.payload?.found_condition ?? null,
        })
    }

    // Resolution — the operator's verdict on an issue item, closing the leg.
    // Only verdict and return transitions (not every state hop, or it gets noisy).
    for (const e of forItem('item_state_changed')) {
        const label = resolutionLabel(e.payload?.from, e.payload?.to)
        if (!label) continue
        rows.push({ key: 'resolution', at: e.occurred_at ?? null, label, reason: e.payload?.reason ?? null })
    }

    // Photographed (draft created at photography completion) + Listed (activated).
    for (const l of lineage.filter(x => x.quote_item_id === quoteItemId)) {
        if (l.drafted_at) rows.push({ key: 'photographed', at: l.drafted_at })
        if (l.activated_at) rows.push({ key: 'listed', at: l.activated_at })
    }

    // Adjusted — an audited discretionary deviation (goodwill/dispute).
    for (const e of forItem('committed_payout_recorded').filter(e => e.payload?.source === 'operator_override')) {
        rows.push({
            key: 'adjusted',
            at: e.occurred_at ?? null,
            deviationCents: e.payload?.deviation_cents ?? null,
            reason: e.payload?.reason ?? null,
        })
    }

    // Paid — settled by a payment (amount = what we committed to pay).
    if (item?.payment_id) {
        const paidEvent = events.find(e =>
            e?.kind === 'payment_completed' && e?.payload?.quote_payment_id === item.payment_id)
        rows.push({ key: 'paid', at: paidEvent?.occurred_at ?? null, amountCents: item?.buy_price_cents ?? null })
    }

    // Chronological order, defensively: sort by the timestamp so any backfilled
    // or out-of-order row lands where its date says; undated rows keep their
    // lifecycle position at the end (stable sort).
    return rows.sort((a, b) => {
        const ea = _epoch(a.at)
        const eb = _epoch(b.at)
        if (ea == null && eb == null) return 0
        if (ea == null) return 1
        if (eb == null) return -1
        return ea - eb
    })
}

// What an operator's move on an item is called in the history, or undefined when
// the hop is not one worth a row. Moves out of `issue` are the verdict; the return
// moves (spec 20261102) are the customer asking for the item back and the answer.
function resolutionLabel(from, to) {
    if (from === 'issue') {
        return { tested_ok: 'Accepted', rejected: 'Rejected', returned: 'Returned to customer', return_requested: 'Return requested' }[to]
    }
    if (from === 'tested_ok' && to === 'return_requested') return 'Return requested'
    if (from === 'return_requested') return { returned: 'Returned to customer', issue: 'Return cancelled' }[to]
    return undefined
}

function _epoch(at) {
    if (!at) return null
    const d = new Date(/^\d+$/.test(String(at)) ? Number(at) : at)
    return isNaN(d.getTime()) ? null : d.getTime()
}

// The item's buy price going into the test: the latest reprice whose current
// buy is known and that landed STRICTLY BEFORE the test, else the quoted offer.
// The cutoff is strict (`>=` is skipped) so a reprice sharing the test's epoch —
// e.g. a same-day POST-test sell-side reprice under date-only timestamps — can
// never be mistaken for the pre-test "before" and fabricate a penalty. A genuine
// same-day pre-test reprice simply falls back to the quoted offer, a safe "before".
// Undated reprices are treated as pre-test (they keep their lifecycle position
// ahead of the tested row).
function _preTestBuyCents(repriceEvents, testedAt, quotedBuyCents) {
    const cutoff = _epoch(testedAt)
    let bestAt = null
    let bestBuy = null
    for (const e of repriceEvents) {
        const buy = e?.payload?.price_after?.current?.buy
        if (buy == null) continue
        const at = _epoch(e?.occurred_at)
        if (cutoff != null && at != null && at >= cutoff) continue
        if (bestAt == null || at == null || at >= bestAt) {
            bestAt = at
            bestBuy = buy
        }
    }
    return bestBuy ?? quotedBuyCents
}

// Row label + one-line description. Pure, so the copy is unit-testable.
export const TIMELINE_LABELS = {
    quoted: 'Quoted', received: 'Received',
    tested: 'Tested', adjusted: 'Adjusted', paid: 'Paid',
    issue: 'Issue', resolution: 'Resolved',
    photographed: 'Photographed', listed: 'Listed on Shopify',
}

// Per-row label wins over the static map (issue/resolution carry a dynamic label).
export function timelineLabel(row) {
    return row.label ?? TIMELINE_LABELS[row.key] ?? ''
}

export function poundsFromCents(cents) {
    return cents == null ? null : `£${(cents / 100).toFixed(2)}`
}

export function describeTimelineRow(r) {
    switch (r.key) {
        case 'quoted':
            return [poundsFromCents(r.amountCents) ?? 'offer made', r.condition].filter(Boolean).join(' · ')
        case 'received':
            return 'arrived in the warehouse'
        case 'tested': {
            const parts = []
            if (r.found) parts.push(r.found)
            // When we know the pre-test BP, show the whole penalty story on one
            // row (BP £68.00 → £20.00 (−£48.00 penalty)) — this is the figure the
            // standalone "Tested: … penalty …" line used to carry. Otherwise fall
            // back to the bare committed payout.
            if (r.originalBpCents != null && r.payoutCents != null && r.originalBpCents !== r.payoutCents) {
                const deviation = r.originalBpCents - r.payoutCents
                const note = deviation > 0 ? `−${poundsFromCents(deviation)} penalty` : `+${poundsFromCents(-deviation)}`
                parts.push(`BP ${poundsFromCents(r.originalBpCents)} → ${poundsFromCents(r.payoutCents)} (${note})`)
            } else if (r.payoutCents != null) {
                parts.push(`payout ${poundsFromCents(r.payoutCents)}`)
            }
            if (r.by) parts.push(`by ${r.by}`)
            return parts.join(' · ') || 'tested'
        }
        case 'adjusted': {
            const amt = r.deviationCents == null ? '' : `${r.deviationCents > 0 ? '+' : ''}${poundsFromCents(r.deviationCents)}`
            return [amt, r.reason].filter(Boolean).join(' · ') || 'adjusted'
        }
        case 'paid':
            return poundsFromCents(r.amountCents) ?? 'paid'
        case 'issue':
            return r.found ?? ''
        case 'resolution':
            return r.reason ?? ''
        case 'photographed':
        case 'listed':
            return ''
        default:
            return ''
    }
}

export function formatTimelineWhen(at) {
    if (!at) return ''
    const d = new Date(/^\d+$/.test(String(at)) ? Number(at) : at)
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
